<template>
    <div class="w-full h-full flex flex-col p-2 pt-4">
        <div class="@container flex items-end">
            <div class="flex items-end gap-2 w-full flex-wrap @4xl:flex-nowrap">
                <div class="w-full max-w-[400px]">
                    <span class="text-sm">Filter</span>
                    <transaction-filter
                        class="w-fit"
                        enable-date-filter
                        :account-no="searchParams.accountNo"
                        @update="onSearchUpdated"
                    />
                </div>
                <div class="w-full">
                    <span class="text-sm">Visning</span>
                    <div class="w-fit flex gap-2 flex-wrap">
                        <UButton
                            icon="i-ph-caret-down-fill"
                            variant="outline"
                            color="neutral"
                            label="Ekspander alle"
                            @click="openAll"
                        />
                        <UButton
                            icon="i-ph-caret-up-fill"
                            variant="outline"
                            color="neutral"
                            label="Kollaps alle"
                            @click="closeAll"
                        />
                    </div>
                </div>
            </div>

            <div class="flex min-w-fit gap-2">
                <UButton
                    variant="outline"
                    color="neutral"
                    label="Eksport CSV"
                    @click="exportCSV"
                />
                <UButton
                    variant="outline"
                    color="neutral"
                    label="Eksport..."
                    @click="showTriBalReportExportsDialog"
                />
            </div>
        </div>
        <div class="flex gap-2 items-end my-2">
            <div class="flex flex-col">
                <span class="text-sm">Finn konto</span>
                <transaction-filter-account
                    :account-no="accountNo"
                    @on-account-no-selected="setAccountActive"
                />
            </div>
        </div>

        <loading-bar :style="{ visibility: loading ? 'visible' : 'hidden' }" data-cy="loading-indicator"/>
        <div class="flex justify-center h-full">
            <ag-grid-vue
                :column-defs="columns"
                :row-data="ledgerAccounts"
                :grid-options="gridOptions"
                :pinned-bottom-row-data="pinnedBottomSumRow"
                class="w-full h-full max-w-screen-4xl"
                :theme="agTheme"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, inject, onMounted, onUnmounted, Ref, ref, shallowRef, watch} from 'vue'
import {hasSelectedOrg, orgSelectionComplete} from '@/tenants/org.service'
import TransactionFilter from '@/transaction/TransactionFilter.vue'
import {Ledger} from '@/types/reports/Ledger'
import {defaultSearchParams, TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import {TrialBalanceTableRow} from '@/types/reports/TrialBalanceTableRow'
import {AgGridVue} from 'ag-grid-vue3'
import {formatCurrency} from '@/services/formatter.service'
import LoadingBar from '@/components/LoadingBar.vue'
import {useEmitter} from '@/services/infrastructure/emitter'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {ORG_SELECTED} from '@/types/events'
import {createTrialBalanceCSVExport} from '@/services/reports/summaryReport.service'
import {download} from '@/services/infrastructure/storage.service'
import _, {noop} from 'lodash'
import noRowsOverlayComponent from '@/components/NoRowsOverlayComponent.vue'
import {agTheme} from '@/agUtils/ag-theme.service'
import {GridApi, GridOptions, GridReadyEvent, RowClassParams, RowHeightParams, ViewportChangedEvent} from 'ag-grid-community'
import {getLedgerByDate, sumLedgerRows} from '@/genleg/ledger.service'
import {globalDefaultColDef} from '@/agUtils'
import {showExportsDialog} from '@/exports/exports.service'
import {ReportType} from '@/types/reports/Report'
import {bookkeepingAccounts} from '@/bookkeeping-account/bookkeepingAccount.service'
import FatTribalDetailRendererSelector from '@/tribal/FatTribalDetailRendererSelector.vue'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {TribalGenDefaultState} from '@/tribal/TribalGenDefaultState'
import TransactionFilterAccount from '@/transaction/TransactionFilterAccount.vue'

type AgCellParam = {
    value?: any,
    data: TrialBalanceTableRow
}

const {onEvent, onEvents, cleanUp} = useEmitter()
const gridApi = shallowRef<GridApi>()

const ledger = ref<Ledger | undefined>(undefined)
const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<number> | undefined = inject('panelPathSegment')
const searchParams = ref<TransactionSearchParams>(defaultSearchParams())
const loading = ref(false)
const visibleRange = ref({startIndex: 0, endIndex: 0})

const activeAccountNo = ref('')

const SET_ACTIVE_ACCOUNT_RETRY_DELAY = 100
const MAX_RETRIES = 20

const props = defineProps({
    accountNo: {
        type: String,
        required: false
    },
    defaultGridState: {
        type: String as () => TribalGenDefaultState,
        required: false,
        default: ''
    },
    fromDate: {
        type: String,
        required: false
    },
    toDate: {
        type: String,
        required: false
    }
})

const ledgerAccounts = computed<LedgerAccount[]>(() => {
    if (!ledger.value) return []
    return ledger.value.ledgerAccounts.map(account => {
        return {
            ...account,
            accountName: bookkeepingAccounts.value.find(a => a.accountNo === account.accountNo)?.name || ''
        }
    })
})

const gridOptions = computed<GridOptions>(() => ({
    animateRows: true,
    masterDetail: true,
    enableCellTextSelection: true,
    noRowsOverlayComponent: noRowsOverlayComponent,
    noRowsOverlayComponentParams: {
        getMessage: () => (loading.value ? 'Laster....' : 'Ingen data for perioden')
    },
    onGridReady: ({api}: GridReadyEvent) => {
        gridApi.value = api
        api.applyColumnState({
            state: [{colId: 'accountNo', sort: 'asc'}]
        })
    },
    onFirstDataRendered() {
        if (props.defaultGridState === 'OPEN') {
            openAll()
        }
    },
    getRowClass: (params: RowClassParams) => {
        if (params.node.rowPinned === 'bottom') {
            return 'text-lg'
        }
        if (!params.node.detail) {
            return 'font-semibold'
        }
    },
    suppressMoveWhenRowDragging: true,
    suppressContextMenu: true,
    getRowId: (params: AgCellParam) => params.data?.accountNo || '',
    defaultColDef: {
        ...globalDefaultColDef,
        sortable: true,
        resizable: true
    },
    context: {
        panelId,
        panelPathSegment,
        searchParams,
        setActiveAccount: (accountNo: string) => {
            activeAccountNo.value = accountNo
        },
        activeAccountNo,
        visibleRange
    },
    getRowHeight: (params: RowHeightParams<LedgerAccount>) => {
        if (!params.node.detail || !params.data) {
            return undefined
        }
        const totalLineCount = params.data.numTransactionLines
        if (activeAccountNo.value === params.data.accountNo) {
            const numLines = Math.min(totalLineCount, 25)
            return 148 + (numLines * 26)
        }
        const numLines = Math.min(totalLineCount, 20)
        return 150 + (numLines * 24)
    },
    popupParent: document.body,
    onViewportChanged(event: ViewportChangedEvent<any>) {
        visibleRange.value = {
            startIndex: event.firstRow,
            endIndex: event.lastRow
        }
    },
    detailCellRenderer: FatTribalDetailRendererSelector
}))

const columns = computed(() => [
    {
        headerName: 'Kontonr',
        field: 'accountNo',
        width: 120,
        cellRenderer: 'agGroupCellRenderer'
    },
    {
        headerName: 'Navn',
        field: 'accountName',
        flex: 1
    },
    {
        headerName: 'Inngående saldo',
        field: 'incomingBalance',
        width: 140,
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right', overflow: 'visible'},
        cellRenderer: currencyCellRenderer
    },
    {
        headerName: 'Endring',
        field: 'movement',
        width: 120,
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right', overflow: 'visible'},
        cellRenderer: currencyCellRenderer
    },
    {
        headerName: 'Utgående saldo',
        field: 'outgoingBalance',
        width: 140,
        headerClass: 'header-right',
        cellStyle: {'text-align': 'right', overflow: 'visible'},
        cellRenderer: currencyCellRenderer
    }
])

const blankPinnedBottomRow = {
    rowIsPinned: true,
    incomingBalance: 0,
    movement: 0,
    outgoingBalance: 0
}

const pinnedBottomSumRow = computed(() => {
    if (!ledgerAccounts.value || ledgerAccounts.value.length === 0) return [blankPinnedBottomRow]

    const totalIncomingBalance = sumLedgerRows(ledgerAccounts.value, 'incomingBalance')
    const totalMovement = sumLedgerRows(ledgerAccounts.value, 'movement')
    const totalOutgoingBalance = sumLedgerRows(ledgerAccounts.value, 'outgoingBalance')

    return [
        {
            rowIsPinned: true,
            incomingBalance: Math.abs(totalIncomingBalance),
            movement: Math.abs(totalMovement),
            outgoingBalance: Math.abs(totalOutgoingBalance)
        }
    ]
})

const fetchLedger = async function (searchParams: TransactionSearchParams) {
    loading.value = true
    ledger.value = await getLedgerByDate(searchParams)
    loading.value = false
}

const setAccountActive = (accountNo: string, retryCount = 0) => {
    const node = gridApi.value?.getRowNode(accountNo)
    if (!node) {
        if (retryCount > MAX_RETRIES) {
            return
        }
        setTimeout(() => {
            setAccountActive(accountNo, retryCount + 1)
        }, SET_ACTIVE_ACCOUNT_RETRY_DELAY)
        return
    }
    activeAccountNo.value = accountNo
    node.setExpanded(true)
    gridApi.value?.ensureNodeVisible(node, 'top')
    gridApi.value?.flashCells({rowNodes: [node]})
}

const exportCSV = async () => {
    if (searchParams.value.toDate && searchParams.value.fromDate) {
        const trialBalanceCSV = await createTrialBalanceCSVExport(searchParams.value.fromDate, searchParams.value.toDate, searchParams.value.department, searchParams.value.project)
        if (trialBalanceCSV) {
            download(trialBalanceCSV).then(noop)
        }
    }
}

const showTriBalReportExportsDialog = () => {
    if (!searchParams.value.fromDate || !searchParams.value.toDate) {
        console.error('From date and to date must be set before exporting the general ledger report.')
        return
    }
    showExportsDialog(
        ReportType.TRIBAL,
        {
            fromDate: searchParams.value.fromDate,
            toDate: searchParams.value.toDate
        }
    )
}

const currencyCellRenderer = (param: AgCellParam) => formatCurrency(param.value, 2)

const onAccountNoPropChange = (newAccountNo: string | undefined) => {
    searchParams.value.accountNo = newAccountNo
    if (newAccountNo) {
        setAccountActive(newAccountNo)
    }
}

const onSearchUpdated = (updatedSearchParams: TransactionSearchParams) => {
    if (_.isEqual(_.omit(updatedSearchParams, 'accountNo'), _.omit(searchParams.value, 'accountNo'))) {
        if (updatedSearchParams.accountNo) {
            setAccountActive(updatedSearchParams.accountNo)
        }
    } else {
        fetchLedger(updatedSearchParams).then(() => {
            if (updatedSearchParams.accountNo) {
                setAccountActive(updatedSearchParams.accountNo)
            }
        })
    }
    searchParams.value = updatedSearchParams

}

const openAll = () => {
    gridApi.value?.forEachNode(node => {
        if (node.master) {
            node.setExpanded(true)
        }
    })
}
const closeAll = () => {
    gridApi.value?.forEachNode(node => {
        if (node.master) {
            node.setExpanded(false)
        }
    })
}

const clear = () => {
    ledger.value = undefined
}

watch(() => props.accountNo, onAccountNoPropChange)

onMounted(async () => {
    await orgSelectionComplete
    if (!hasSelectedOrg()) {
        console.error('Accessing the ledger page without selected org. Should not be possible, this is a bug!')
    }
    onEvents([TRANSACTION_CREATED, TRANSACTION_UPDATED, DELETE_TRANSACTION], () => {
        fetchLedger(searchParams.value)
    })
    onEvent(ORG_SELECTED, () => {
        clear()
        fetchLedger(searchParams.value)
    })
    if (props.fromDate) {
        searchParams.value.fromDate = props.fromDate
    }
    if (props.toDate) {
        searchParams.value.toDate = props.toDate
    }
    if (props.accountNo) {
        onAccountNoPropChange(props.accountNo)
    }
})

onUnmounted(cleanUp)
</script>

<style>

</style>
